@file:Suppress("FunctionName")

package components

import kotlinx.browser.document
import kotlinx.coroutines.launch
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.div
import kotlinx.html.js.onChangeFunction
import mainScope
import net.gorillagroove.api.UserId
import onCheckedFunction
import net.gorillagroove.user.User
import net.gorillagroove.user.UserService
import net.gorillagroove.user.UserVisibilityType
import net.gorillagroove.user.UserVisibilityUpdate
import net.gorillagroove.util.GGLog.logError
import org.w3c.dom.HTMLInputElement

private fun User.isCurrentlyBlocked() = yourVisibility == UserVisibilityType.BLOCKED
private fun checkboxId(user: User) = "user-visibility-checkbox-${user.id.value}"

fun UserVisibilityModal() = document.create.div {
    id = "user-visibility-modal"

    h3 {
        + "User Visibility"

        i("fa-solid fa-circle-info ml-6") {
            tooltip = "Blocking a user prevents them from seeing your activity,\nrecommending you songs, or inviting you to their playlists.\nIt also prevents you from doing the same to them.\n\nExisting shared playlists must be handled manually"
            tooltipDelay = 100
        }
    }

    div("mb-12") {
        input(InputType.checkBox) {
            checked = UserService.hideNewUsers

            onCheckedFunction = { value ->
                UserService.hideNewUsers = value
            }
        }

        + " Block new users"
    }

    val ownUserId = UserService.requireCurrentUserId()
    val users = UserService.findExcludingIds(listOf(ownUserId), excludeInactive = false, excludeHidden = false)

    // Toggling a checkbox back to its original state removes it, so this only ever holds real changes.
    val pendingChanges = mutableMapOf<UserId, Boolean>()

    fun updatePendingChange(user: User, newValue: Boolean) {
        if (newValue == user.isCurrentlyBlocked()) {
            pendingChanges.remove(user.id)
        } else {
            pendingChanges[user.id] = newValue
        }
    }

    fun recomputeSelectAllState() {
        val selectAllCheckbox = document.getElementById("user-visibility-select-all") as HTMLInputElement
        val blockedCount = users.count { (document.getElementById(checkboxId(it)) as HTMLInputElement).checked }

        when (blockedCount) {
            0 -> {
                selectAllCheckbox.indeterminate = false
                selectAllCheckbox.checked = false
            }
            users.size -> {
                selectAllCheckbox.indeterminate = false
                selectAllCheckbox.checked = true
            }
            else -> selectAllCheckbox.indeterminate = true
        }
    }

    table("data-table mt-12") {
        thead {
            tr {
                th { + "Name" }
                th(classes = "minimize-width") {
                    + "Blocked "

                    input(InputType.checkBox) {
                        id = "user-visibility-select-all"

                        onChangeFunction = { event ->
                            val newValue = (event.currentTarget as HTMLInputElement).checked

                            users.forEach { user ->
                                updatePendingChange(user, newValue)

                                val checkbox = document.getElementById(checkboxId(user)) as HTMLInputElement
                                checkbox.checked = newValue
                            }
                        }
                    }
                }
            }
        }
        tbody {
            users.forEach { user ->
                tr {
                    id = "user-visibility-row-${user.id.value}"

                    td { + user.name }
                    td(classes = "minimize-width") {
                        input(InputType.checkBox) {
                            id = checkboxId(user)
                            checked = user.isCurrentlyBlocked()

                            onChangeFunction = { event ->
                                val newValue = (event.currentTarget as HTMLInputElement).checked
                                updatePendingChange(user, newValue)
                                recomputeSelectAllState()
                            }
                        }
                    }
                }
            }
        }
    }

    mainScope.launch {
        recomputeSelectAllState()
    }

    div("mt-12") {
        ActionButton("user-visibility-save-button", "Save") {
            if (pendingChanges.isEmpty()) {
                Toast.info("Nothing was changed")
                Dialog.remove()
                return@ActionButton
            }

            val updates = pendingChanges.map { (userId, hidden) -> UserVisibilityUpdate(userId, hidden) }

            try {
                UserService.updateUserVisibility(updates)
            } catch (e: Exception) {
                Toast.error("Failed to update user visibility")
                logError("Failed to update user visibility!", e)
                return@ActionButton
            }

            Toast.success("User visibility updated")
            Dialog.remove()
        }
    }
}.also { Tooltip.registerAll(it) }
